import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react';
import './DatePickerButton.css';

const WEEKDAYS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];

function parseIsoDate(iso) {
    if (!iso) return null;
    const [year, month, day] = iso.split('-').map(Number);
    if (!year || !month || !day) return null;
    return new Date(year, month - 1, day);
}

function toIsoDate(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}

function sameDay(a, b) {
    return a && b
        && a.getFullYear() === b.getFullYear()
        && a.getMonth() === b.getMonth()
        && a.getDate() === b.getDate();
}

function monthGrid(monthDate) {
    const first = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
    const startOffset = (first.getDay() + 6) % 7;
    const start = new Date(first);
    start.setDate(first.getDate() - startOffset);
    return Array.from({ length: 42 }, (_, index) => {
        const day = new Date(start);
        day.setDate(start.getDate() + index);
        return day;
    });
}

function monthTitle(date) {
    const title = date.toLocaleDateString('ru-RU', { month: 'long', year: 'numeric' });
    return title.charAt(0).toUpperCase() + title.slice(1);
}

const DatePickerButton = ({ value, onChange, label = 'Выбрать дату' }) => {
    const buttonRef = useRef(null);
    const popoverRef = useRef(null);
    const [open, setOpen] = useState(false);
    const [month, setMonth] = useState(() => parseIsoDate(value) || new Date());
    const [position, setPosition] = useState({ top: 0, left: 0 });

    useEffect(() => {
        if (!open) return undefined;
        setMonth(parseIsoDate(value) || new Date());

        const place = () => {
            const rect = buttonRef.current?.getBoundingClientRect();
            if (!rect) return;
            const width = 280;
            const height = 320;
            let left = rect.right - width;
            if (left < 8) left = 8;
            if (left + width > window.innerWidth - 8) left = window.innerWidth - width - 8;
            let top = rect.bottom + 6;
            if (top + height > window.innerHeight - 8) top = Math.max(8, rect.top - height - 6);
            setPosition({ top, left });
        };

        place();
        const onPointerDown = (event) => {
            if (popoverRef.current?.contains(event.target) || buttonRef.current?.contains(event.target)) return;
            setOpen(false);
        };
        const onKeyDown = (event) => {
            if (event.key === 'Escape') setOpen(false);
        };
        document.addEventListener('mousedown', onPointerDown);
        document.addEventListener('keydown', onKeyDown);
        window.addEventListener('resize', place);
        window.addEventListener('scroll', place, true);
        return () => {
            document.removeEventListener('mousedown', onPointerDown);
            document.removeEventListener('keydown', onKeyDown);
            window.removeEventListener('resize', place);
            window.removeEventListener('scroll', place, true);
        };
    }, [open, value]);

    const selected = parseIsoDate(value);
    const today = new Date();

    return (
        <>
            <button
                ref={buttonRef}
                type="button"
                className="date-picker-button"
                aria-label={label}
                aria-expanded={open}
                onClick={() => setOpen((current) => !current)}
            >
                <Calendar size={18} />
            </button>
            {open && createPortal(
                <div
                    ref={popoverRef}
                    className="date-picker-popover"
                    style={{ top: position.top, left: position.left }}
                    role="dialog"
                    aria-label={label}
                >
                    <div className="date-picker-head">
                        <button type="button" aria-label="Предыдущий месяц" onClick={() => setMonth((current) => new Date(current.getFullYear(), current.getMonth() - 1, 1))}>
                            <ChevronLeft size={18} />
                        </button>
                        <span>{monthTitle(month)}</span>
                        <button type="button" aria-label="Следующий месяц" onClick={() => setMonth((current) => new Date(current.getFullYear(), current.getMonth() + 1, 1))}>
                            <ChevronRight size={18} />
                        </button>
                    </div>
                    <div className="date-picker-weekdays">
                        {WEEKDAYS.map((day) => <span key={day}>{day}</span>)}
                    </div>
                    <div className="date-picker-grid">
                        {monthGrid(month).map((day) => {
                            const outside = day.getMonth() !== month.getMonth();
                            const className = [
                                'date-picker-day',
                                outside ? 'is-outside' : '',
                                sameDay(day, selected) ? 'is-selected' : '',
                                sameDay(day, today) ? 'is-today' : ''
                            ].filter(Boolean).join(' ');
                            return (
                                <button
                                    type="button"
                                    key={toIsoDate(day)}
                                    className={className}
                                    onClick={() => {
                                        onChange(toIsoDate(day));
                                        setOpen(false);
                                    }}
                                >
                                    {day.getDate()}
                                </button>
                            );
                        })}
                    </div>
                </div>,
                document.body
            )}
        </>
    );
};

export default DatePickerButton;
